import type { CanonicalTransaction, DashboardIndexes } from "./types";

export type EntityRef =
  | { kind: "issuer"; id: string }
  | { kind: "owner"; id: string }
  | { kind: "cluster"; id: string };

export interface EntityDossierSummary {
  ref: EntityRef;
  title: string;
  subtitle: string;
  rowCount: number;
  informativeBuyCount: number;
  informativeSellCount: number;
  highScoreBuyCount: number;
  clusterRowCount: number;
  totalValue: number | null;
  distinctInsiders: number;
  distinctTickers: number;
  plannedCount: number;
  latestFilingDate: string | null;
  rows: CanonicalTransaction[];
}

function sumValues(rows: readonly CanonicalTransaction[]): number | null {
  let total = 0;
  let saw = false;
  for (const row of rows) {
    if (row.value === null) continue;
    total += row.value;
    saw = true;
  }
  return saw ? total : null;
}

export function resolveEntityRows(
  ref: EntityRef,
  indexes: DashboardIndexes
): CanonicalTransaction[] {
  if (ref.kind === "issuer") return indexes.byIssuer.get(ref.id) ?? [];
  if (ref.kind === "owner") return indexes.byOwner.get(ref.id) ?? [];
  return indexes.byCluster.get(ref.id) ?? [];
}

export function buildEntityDossier(
  ref: EntityRef,
  indexes: DashboardIndexes
): EntityDossierSummary {
  const rows = resolveEntityRows(ref, indexes);
  const informativeBuyCount = rows.filter((row) => row.signal.bucket === "informative_buy").length;
  const informativeSellCount = rows.filter((row) => row.signal.bucket === "informative_sell").length;
  const highScoreBuyCount = rows.filter(
    (row) => row.signal.bucket === "informative_buy" && (row.signal.score ?? 0) >= 75
  ).length;
  const clusterRowCount = rows.filter(
    (row) => row.cluster !== null && row.cluster.distinctInsiderCount >= 2
  ).length;
  const plannedCount = rows.filter((row) => row.plan.status === "planned").length;
  const distinctInsiders = new Set(
    rows.map((row) => row.ownerIdentityKey ?? row.insiderName).filter(Boolean)
  ).size;
  const distinctTickers = new Set(rows.map((row) => row.ticker)).size;
  const latestFilingDate = rows.reduce<string | null>((latest, row) => {
    if (latest === null || row.filingDate > latest) return row.filingDate;
    return latest;
  }, null);

  if (ref.kind === "issuer") {
    const sample = rows[0];
    return {
      ref,
      title: sample?.companyName ?? `Issuer ${ref.id}`,
      subtitle: sample ? `${sample.ticker} · CIK ${ref.id}` : `CIK ${ref.id}`,
      rowCount: rows.length,
      informativeBuyCount,
      informativeSellCount,
      highScoreBuyCount,
      clusterRowCount,
      totalValue: sumValues(rows),
      distinctInsiders,
      distinctTickers,
      plannedCount,
      latestFilingDate,
      rows
    };
  }

  if (ref.kind === "owner") {
    const sample = rows[0];
    return {
      ref,
      title: sample?.insiderName ?? `Owner ${ref.id}`,
      subtitle: sample?.relationship || ref.id,
      rowCount: rows.length,
      informativeBuyCount,
      informativeSellCount,
      highScoreBuyCount,
      clusterRowCount,
      totalValue: sumValues(rows),
      distinctInsiders,
      distinctTickers,
      plannedCount,
      latestFilingDate,
      rows
    };
  }

  const sample = rows[0];
  return {
    ref,
    title: `Cluster ${ref.id.slice(0, 12)}`,
    subtitle: sample
      ? `${sample.ticker} · ${sample.cluster?.distinctInsiderCount ?? distinctInsiders} insiders`
      : ref.id,
    rowCount: rows.length,
    informativeBuyCount,
    informativeSellCount,
    highScoreBuyCount,
    clusterRowCount,
    totalValue: sumValues(rows),
    distinctInsiders,
    distinctTickers,
    plannedCount,
    latestFilingDate,
    rows
  };
}

export function parseEntitySearchParam(value: string | null): EntityRef | null {
  if (!value) return null;
  const [kind, ...rest] = value.split(":");
  const id = rest.join(":").trim();
  if (!id) return null;
  if (kind === "issuer" || kind === "owner" || kind === "cluster") {
    return { kind, id };
  }
  return null;
}

export function serializeEntitySearchParam(ref: EntityRef | null): string | null {
  return ref ? `${ref.kind}:${ref.id}` : null;
}
